import { Tonality } from '@dsny/dsny-component-library';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { convertDateToLocalTime } from 'src/utils/Formatter';
import {
  ComboboxInputStyles,
  ComboboxPopoverStyles,
  ComboboxStyles,
  ComboboxWrapper,
  ErrorComboboxInputStyles,
  ErrorInputLabelStyles,
  InlineErrorMessage,
  InlineNote,
  InputLabelStyles,
  StyledDayPicker,
} from './StartTrackingSelection.styles';
import { AddTrackObj } from '../MultiAsset.slice';

type StartTrackingProps = {
  displayDate: Date | string;
  handleStartDate: (date: Date) => void;
  isCasterRelase: boolean;
  activeTracks: AddTrackObj[];
};

const StartTrackingSelection: React.FC<StartTrackingProps> = ({
  displayDate,
  handleStartDate,
  isCasterRelase,
  activeTracks,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [validationError, setValidationError] = useState(false);

  const openCombobox = () => {
    setOpen(true);
  };

  const closeCombobox = () => {
    setOpen(false);
  };

  // Handling disable date
  const getDisableDate = (days?: number) => {
    const date = new Date();
    if (days) {
      date.setDate(date.getDate() + days);
    }
    return date;
  };

  // check for validation errors when caster song
  const hasValidationErrorStyle = () => {
    if (isCasterRelase && activeTracks.length === 1) {
      return (
        activeTracks[0].isFileUploadError &&
        activeTracks[0].startTrackingDate === undefined
      );
    }

    return false;
  };

  // Handling date selection
  const handleDateSelection = (date: Date) => {
    handleStartDate(date);
    setOpen(false);
  };

  // Handling onchange
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleDateSelection(new Date(e.target.value));
  };

  // Handling get start date
  const getStartDate = () => {
    if (isCasterRelase && activeTracks.length === 1) {
      return activeTracks[0].startTrackingDate
        ? format(activeTracks[0].startTrackingDate, 'MMMM dd, yyyy')
        : '';
    }

    if (displayDate === '') {
      return displayDate;
    }

    return format(displayDate as Date, 'MMMM dd, yyyy');
  };

  useEffect(() => {
    setValidationError(hasValidationErrorStyle());
  }, [activeTracks]);

  return (
    <>
      {/* Start Tracking Selection - Day Picker */}
      <ComboboxWrapper>
        <Tonality.Combobox
          isOpen={open}
          onClick={openCombobox}
          onClose={closeCombobox}
          onChange={onChange}
          label={t('MULTI_ASSET_START_TRACKING_DATE')}
          value={getStartDate()}
          style={{ ...ComboboxStyles }}
          setting="mtr"
        >
          <Tonality.ComboboxInput
            style={
              validationError
                ? { ...ErrorComboboxInputStyles }
                : { ...ComboboxInputStyles }
            }
            tooltip={t('MULTI_ASSET_START_TRACKING_DATE_TOOLTIP')}
            required
            hasError={false}
            inputLabelStyles={
              validationError
                ? { ...ErrorInputLabelStyles }
                : { ...InputLabelStyles }
            }
            placeholder={validationError ? 'Please select a date' : ''}
          />
          <Tonality.ComboboxPopover style={{ ...ComboboxPopoverStyles }}>
            <StyledDayPicker
              fromMonth={getDisableDate()}
              toMonth={getDisableDate(30)}
              disabled={[
                { before: getDisableDate() },
                { after: getDisableDate(30) },
              ]}
              selected={[new Date(displayDate)]}
              onDayClick={(nextDay) => {
                handleDateSelection(nextDay);
              }}
            />
          </Tonality.ComboboxPopover>

          {/* Inline error message - Caster release flow */}
          {validationError && (
            <InlineErrorMessage>
              {t('MULTI_ASSET_START_TRACKING_INLINE_ERROR')}
            </InlineErrorMessage>
          )}

          {/* Inline Note */}
          {isCasterRelase && activeTracks[0]?.release_date ? (
            /* 2. Caster songs */
            <InlineNote>
              <Trans
                i18nKey="IMPORT_RELEASE_START_TRACKING_INLINE_NOTE"
                values={{
                  releaseDate: format(
                    convertDateToLocalTime(
                      new Date(Date.parse(activeTracks[0]?.release_date))
                    ),
                    'MMM dd, yyyy'
                  ),
                }}
              />
            </InlineNote>
          ) : (
            /* 1. Default flow */
            <InlineNote>
              {t('MULTI_ASSET_START_TRACKING_INLINE_NOTE')}
            </InlineNote>
          )}
        </Tonality.Combobox>
      </ComboboxWrapper>
    </>
  );
};

export default StartTrackingSelection;
